import { Navigate, useSearchParams } from 'react-router-dom';
// Do subcaminho, e não de `@mpa/shared`: o Vite resolve `@mpa/shared/*` no
// código-fonte, mas o pacote inteiro não está mapeado — e a diferença só
// aparece no build, porque o `tsc` resolve os dois.
import { PERMISSIONS } from '@mpa/shared/permissions';
import { useAuth } from '../auth/auth-context';
import { PageHeader, Vazio } from '../components/ui';
import { ConfigOrganizacao } from './ConfigOrganizacao';
import { ConfigWhatsapp } from './WhatsApp';
import { ConfigUsuarios } from './Usuarios';
import { ConfigAuditoria } from './Auditoria';

/**
 * Um lugar só para o que se configura uma vez e quase não se mexe.
 *
 * Eram três itens soltos na barra lateral — WhatsApp, Usuários, Auditoria —
 * competindo em tamanho com Eventos, que é o trabalho do dia. Configuração não
 * disputa atenção com operação: ela precisa ser encontrável, não visível o
 * tempo todo.
 *
 * A quarta aba é nova: os dados da própria empresa, que até aqui só o
 * Superadmin editava pelo painel Master.
 */

type Aba = 'organizacao' | 'whatsapp' | 'usuarios' | 'auditoria';

const ABAS: { id: Aba; rotulo: string; icone: string; permissao: string }[] = [
  {
    id: 'organizacao',
    rotulo: 'Organização',
    icone: 'ti-building',
    permissao: PERMISSIONS.SETTINGS_MANAGE,
  },
  {
    id: 'whatsapp',
    rotulo: 'WhatsApp',
    icone: 'ti-brand-whatsapp',
    permissao: PERMISSIONS.INTEGRATIONS_MANAGE,
  },
  { id: 'usuarios', rotulo: 'Usuários', icone: 'ti-users', permissao: PERMISSIONS.USERS_MANAGE },
  { id: 'auditoria', rotulo: 'Auditoria', icone: 'ti-history', permissao: PERMISSIONS.AUDIT_READ },
];

export function Configuracoes() {
  const { pode } = useAuth();
  const [busca, setBusca] = useSearchParams();

  /**
   * As abas seguem a permissão, uma a uma.
   *
   * A rota inteira não pode exigir as quatro: quem administra a equipe mas não
   * mexe na integração ficaria de fora de tudo. E não pode dispensar todas: a
   * aba apareceria e cada consulta dentro dela responderia 403.
   */
  const visiveis = ABAS.filter((a) => pode(a.permissao));

  const pedida = busca.get('aba') as Aba | null;
  // Aba pedida que a pessoa não alcança cai na primeira que ela alcança, em vez
  // de mostrar um bloco vazio — o link pode ter vindo de um colega com outro
  // papel.
  const aba = visiveis.find((a) => a.id === pedida)?.id ?? visiveis[0]?.id;

  if (!aba) {
    return (
      <>
        <PageHeader titulo="Configurações" />
        <Vazio
          icone="ti-lock"
          titulo="Nada para configurar com o seu papel"
          descricao="Integração, equipe, auditoria e dados da empresa exigem permissões que o seu papel não inclui. Fale com o administrador da sua organização."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        titulo="Configurações"
        subtitulo="Dados da empresa, integração de WhatsApp, equipe e registro de atividade."
      />

      <div className="abas">
        {visiveis.map((a) => (
          <button
            key={a.id}
            className={`aba${aba === a.id ? ' ativa' : ''}`}
            onClick={() => setBusca({ aba: a.id }, { replace: true })}
          >
            <i className={`ti ${a.icone}`} /> {a.rotulo}
          </button>
        ))}
      </div>

      {aba === 'organizacao' && <ConfigOrganizacao />}
      {aba === 'whatsapp' && <ConfigWhatsapp />}
      {aba === 'usuarios' && <ConfigUsuarios />}
      {aba === 'auditoria' && <ConfigAuditoria />}
    </>
  );
}

/**
 * Os endereços antigos, um por tela, agora levam à aba correspondente.
 *
 * Cada um tinha item próprio na barra lateral por meses: é endereço que está em
 * favorito e em aba aberta, e um 404 não conta que a tela mudou de lugar.
 */
export function RedirecionarConfiguracao({ aba }: { aba: Aba }) {
  return <Navigate to={`/configuracoes?aba=${aba}`} replace />;
}
